/**
 * The Stripe payment component and related helpers.
 * Stripe.js type definitions. There didn't appear to be an up-to-date package
 * in npm for us to use.
 */

import { h as htm } from 'minidoc-editor';
import { useMemo, useEffect, useRef } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { ManualDom } from '@components/manual-dom';
import { useAsyncData } from 'client/lib/hooks';
import { dispatchClearError } from '@components/async-form';
import { useCurrentTenant } from '@components/router/session-context';
import {
  loadStripe,
  Stripe,
  StripeElements,
  StripeCardElement,
  StripeError,
} from '@stripe/stripe-js';
import { useIntl } from 'shared/intl/use-intl';
import { reportCheckoutError } from './report-checkout-error';

export type StripeJs = Stripe;

export interface StripeContext {
  stripe: Stripe;
  elements: StripeElements;
  isCardComplete(): boolean;
}

export function isStripeCardError(err: unknown): err is StripeError {
  const type = (err as StripeError | undefined)?.type;
  return type === 'card_error' || type === 'validation_error';
}

type StripeLoadResult = { stripe: Stripe; error?: undefined } | { stripe?: undefined; error: true };

interface Props {
  priceId: string;
  customerName: string;
  onReady(c: StripeContext): void;
  collectAddress: boolean;
  disabled?: boolean;
}

/**
 * Load and render the StripeJS card element.
 */
export function StripeInput({ customerName, priceId, onReady, collectAddress, disabled }: Props) {
  const intl = useIntl();
  const tenant = useCurrentTenant();
  const cardWrapper = useMemo(() => htm('div'), []);
  const addrWrapper = useMemo(() => htm('div.mt-4'), []);
  const cardElementRef = useRef<StripeCardElement | null>(null);

  const cfg = useAsyncData(async (): Promise<StripeLoadResult> => {
    try {
      // Load stripe.js and wait for it to become available, then mount the
      // the initialized Stripe object: https://stripe.com/docs/stripe-js
      const config = await rpx.payments.getStripeClientConfig({ priceId });
      const stripe = await loadStripe(config.stripePublishableKey!, {
        stripeAccount: config.stripeAccount,
        locale: tenant.locale,
      });
      if (!stripe) {
        throw new Error(`Failed to load Stripe.`);
      }
      const elements = stripe.elements({
        appearance: {
          theme: 'flat',
          variables: {
            colorBackground: '#fff',
            borderRadius: '0.25rem',
            focusBoxShadow: 'none',
            focusOutline: '2px solid #4f46e5',
          },
          rules: {
            '.Input': {
              border: '1px solid #e2e8f0',
              padding: '0.5rem',
            },
            '.Input--invalid': {
              color: '#ef4444',
            },
          },
        },
      });
      const cardEl = elements.create('card', {
        classes: {
          base: 'border rounded-sm p-2',
          focus: 'ring-2 ring-indigo-600',
        },
        disabled: disabled || false,
      });

      // Store reference to card element
      cardElementRef.current = cardEl;

      let isCardComplete = false;

      cardEl.on('ready', () => {
        onReady({ stripe, elements, isCardComplete: () => isCardComplete });
        if (!disabled) {
          cardEl.focus();
        }
      });
      cardEl.on('change', (e) => {
        isCardComplete = e.complete;
        dispatchClearError(cardWrapper, 'paymentMethod');
      });
      cardEl.mount(cardWrapper);
      if (collectAddress) {
        const addrEl = elements.create('address', {
          mode: 'shipping',
          defaultValues: {
            name: customerName,
          },
        });
        addrEl.mount(addrWrapper);
        cardEl.on('blur', () => addrEl.focus());
      }
      return { stripe };
    } catch (err) {
      reportCheckoutError({ step: 'load', priceId, err });
      return { error: true };
    }
  }, [priceId]);

  const stripe = cfg.data?.stripe;
  const hasLoadError = !!cfg.data?.error;

  // Update card element disabled state when prop changes
  useEffect(() => {
    if (cardElementRef.current && stripe) {
      cardElementRef.current.update({ disabled: disabled || false });
    }
  }, [disabled, stripe]);

  return (
    <section
      class={`mb-1 ${!stripe && !hasLoadError ? 'animate-pulse' : ''} relative`}
      data-private
    >
      {!stripe && !hasLoadError && (
        <div class="py-1.5 px-2 w-full bg-gray-200 text-gray-400 rounded-sm text-center">
          loading...
        </div>
      )}
      {hasLoadError && (
        <div class="py-1.5 px-2 w-full text-red-600 text-center">
          {intl('Payment form failed to load. Refresh the page to try again.')}
        </div>
      )}
      {!!stripe && <ManualDom el={cardWrapper} />}
      {!!stripe && collectAddress && <ManualDom el={addrWrapper} />}
      {disabled && !!stripe && (
        <div class="absolute inset-0 bg-white bg-opacity-95 rounded-sm flex items-center justify-center">
          <div class="flex items-center gap-3 text-gray-700">
            <div class="w-5 h-5 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></div>
            <span class="font-medium">Processing payment...</span>
          </div>
        </div>
      )}
    </section>
  );
}
